import { RichTextField, useRecordContext } from 'react-admin';
import get from 'lodash/get';
import { COUNTRY_CODE_TO_FLAG } from '@/constants';
import { RejectedReasonsTable } from '@/pages/AdverseAction/view/tables';
import {
  boolLabel,
  fieldIf,
  formatPhone,
  getAdverseAttachmentFields,
  text,
} from '@/pages/components/SummaryDetails/utils';
import { path } from '@/pages/AdverseAction/constants';
import { FCRAWorkerAdverse } from '@/types/turn-api';
import type {
  CardSectionVM,
  FieldVM,
  HeaderVM,
  SummaryDetailsController,
  TableFieldVM,
  TableSectionVM,
} from '@/pages/components/SummaryDetails/types';
import {
  faBoxes,
  faBuildings,
  faClipboardList,
  faClipboardUser,
  faEnvelope,
  faLocationDot,
  faNote,
  faPaperclip,
} from '@fortawesome/pro-regular-svg-icons';

const useController = (): SummaryDetailsController => {
  const r = useRecordContext<FCRAWorkerAdverse.Details>();

  // sources
  const sAdverseAttachments = path('adverse.adverse_attachments');
  const sAdverseComments = path('adverse.comments');
  const sAdverseDispute = path('adverse.dispute');
  const sAdverseStatus = path('adverse.status');
  const sDisplayName = path('display_name');
  const sCity = path('location.city');
  const sEmail = path('candidate_email');
  const sId = path('id');
  const sNote = path('note');
  const sPackageCountry = path('package.country_code');
  const sPackageId = path('package.package_id');
  const sPackageName = path('package.name');
  const sPartnerId = path('partner.id');
  const sPartnerName = path('partner.name');
  const sPhone = path('phone_number');
  const sPreadverseAttachments = path('adverse.preadverse_attachments');
  const sProfileStatus = path('profile_status');
  const sReportUrl = path('report_url');
  const sSpecificReasons = path('adverse.specific_reasons');
  const sState = path('location.state');
  const sTurnId = path('turn_id');
  const sZip = path('zipcode');

  const countryCodeField = fieldIf(r, sPackageCountry, {
    key: sPackageCountry,
    label: 'Country Code',
    value: text(r, sPackageCountry),
  });

  const countryFlag = COUNTRY_CODE_TO_FLAG?.[(countryCodeField as FieldVM)?.value as string];

  const header: HeaderVM = {
    countryFlag,
    displayName: text(r, sDisplayName),
    id: text(r, sId),
    profileStatus: sProfileStatus,
    turnId: text(r, sTurnId),
    url: {
      href: text(r, sReportUrl),
      label: 'Report URL',
    },
  };

  const locationSection: CardSectionVM = {
    title: 'Location',
    icon: faLocationDot,
    fields: [
      fieldIf(r, sCity, { key: sCity, label: 'City', value: text(r, sCity) })!,
      fieldIf(r, sState, { key: sState, label: 'State', value: text(r, sState) })!,
      fieldIf(r, sZip, { key: sZip, label: 'Zipcode', value: text(r, sZip), copyText: text(r, sZip) })!,
    ].filter(Boolean) as FieldVM[],
  };

  const contactSection: CardSectionVM = {
    title: 'Contact',
    icon: faEnvelope,
    fields: [
      fieldIf(r, sEmail, { key: sEmail, label: 'Email', value: text(r, sEmail), copyText: text(r, sEmail) })!,
      fieldIf(r, sPhone, {
        key: sPhone,
        label: 'Phone Number',
        value: formatPhone(text(r, sPhone)),
        copyText: text(r, sPhone),
      })!,
    ].filter(Boolean) as FieldVM[],
  };

  const partnerSection: CardSectionVM = {
    title: 'Partner',
    icon: faBuildings,
    fields: [
      fieldIf(r, sPartnerId, {
        key: sPartnerId,
        label: 'ID',
        value: text(r, sPartnerId),
        copyText: text(r, sPartnerId),
      })!,
      fieldIf(r, sPartnerName, { key: sPartnerName, label: 'Name', value: text(r, sPartnerName) })!,
    ].filter(Boolean) as FieldVM[],
  };

  const packageSection: CardSectionVM = {
    title: 'Package',
    icon: faBoxes,
    fields: [
      fieldIf(r, sPackageId, {
        key: sPackageId,
        label: 'ID',
        value: text(r, sPackageId),
        copyText: text(r, sPackageId),
      })!,
      fieldIf(r, sPackageName, { key: sPackageName, label: 'Name', value: text(r, sPackageName) })!,
      countryCodeField!,
    ].filter(Boolean) as FieldVM[],
  };

  const checksSection: CardSectionVM = {
    title: 'BGC Info',
    icon: faClipboardUser,
    fields: [
      fieldIf(r, sAdverseStatus, { key: sAdverseStatus, label: 'Adverse Status', value: text(r, sAdverseStatus) })!,
      fieldIf(r, sAdverseDispute, {
        key: sAdverseDispute,
        label: 'Dispute',
        value: boolLabel(get(r, sAdverseDispute)),
      })!,
      fieldIf(r, sAdverseComments, { key: sAdverseComments, label: 'Comments', value: text(r, sAdverseComments) })!,
    ].filter(Boolean) as FieldVM[],
  };

  const attachmentsSection: CardSectionVM = {
    title: 'Attachments',
    icon: faPaperclip,
    fields: [
      fieldIf(r, sAdverseAttachments, {
        customValue: getAdverseAttachmentFields(r, sAdverseAttachments),
        key: sAdverseAttachments,
        label: 'Adverse',
      })!,
      fieldIf(r, sPreadverseAttachments, {
        customValue: getAdverseAttachmentFields(r, sPreadverseAttachments),
        key: sPreadverseAttachments,
        label: 'Preadverse',
      })!,
    ].filter(Boolean) as FieldVM[],
  };

  const noteSection: CardSectionVM = {
    title: 'Reject Reason',
    icon: faNote,
    fields: [fieldIf(r, sNote, { key: sNote, customValue: <RichTextField source={sNote} /> })!].filter(
      Boolean
    ) as FieldVM[],
  };

  const tablesSection: TableSectionVM = {
    title: 'Tables',
    sx: { width: 965 },
    fields: [
      fieldIf(r, sSpecificReasons, {
        component: <RejectedReasonsTable source={sSpecificReasons} />,
        icon: faClipboardList,
        label: 'Rejected Reasons',
        sx: { card: { width: '100%' } },
      })!,
    ].filter(Boolean) as TableFieldVM[],
  };

  return {
    header,
    leftColumn: [locationSection, partnerSection, checksSection, attachmentsSection],
    rightColumn: [contactSection, packageSection, noteSection],
    tablesSection,
  };
};

export default useController;
